import moment from 'moment';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { CardComment } from './TaskBoardCardModal';
import { buildMemberNameMap, formatCommentTime, renderCommentWithMentions } from './task-board.comments';

type LabelItem = { text: string; color: string };
type ViewMember = {
  userId: string;
  user?: { id: string; firstName: string; lastName: string } | null;
};
type ViewChecklistItem = { id?: string; text: string; isDone: boolean; position: number };
type ViewChecklist = { id?: string; title: string; items: ViewChecklistItem[] };
type ViewAttachment = { id: string; fileName: string; fileMimetype: string; signedUrl: string };

export type ConcludedCardDetail = {
  id: string;
  title: string;
  description?: string | null;
  dueDate?: string | null;
  simpleLabels?: string[];
  labelsData?: LabelItem[] | null;
  members?: ViewMember[];
  checklists?: ViewChecklist[];
  attachments?: ViewAttachment[];
  commentsCount?: number;
  isRecurrenceTemplate?: boolean;
  isRecurrenceInstance?: boolean;
};

type Props = {
  isOpen: boolean;
  card: ConcludedCardDetail | null;
  onClose: () => void;
  onRestore: (card: ConcludedCardDetail) => void;
  comments?: CardComment[];
  loadingComments?: boolean;
  boardMembers?: ViewMember[];
};

function parseDueDate(dateValue: string) {
  return moment(String(dateValue).slice(0, 10), 'YYYY-MM-DD');
}

function formatDateToUs(dateValue: string): string {
  const m = parseDueDate(dateValue);
  return m.isValid() ? m.format('MM/DD/YYYY') : dateValue;
}

const TaskBoardCardViewModal = ({
  isOpen,
  card,
  onClose,
  onRestore,
  comments = [],
  loadingComments = false,
  boardMembers = [],
}: Props) => {
  if (!card) return null;

  const dueBadge = (() => {
    if (!card.dueDate) return null;
    const due = parseDueDate(String(card.dueDate));
    if (!due.isValid()) return { label: formatDateToUs(card.dueDate), variant: 'muted' };
    const today = moment().startOf('day');
    const dueDay = due.clone().startOf('day');
    const label = formatDateToUs(card.dueDate);
    if (dueDay.isBefore(today)) return { label: `Overdue: ${label}`, variant: 'danger' };
    if (dueDay.isSame(today)) return { label: 'Due today', variant: 'warning' };
    return { label: `Due: ${label}`, variant: 'success' };
  })();

  const checklists = card.checklists || [];
  const members = card.members || [];
  const simpleLabels = card.simpleLabels || [];
  const labelsData = card.labelsData || [];
  const attachments = card.attachments || [];
  const mentionNameMap = buildMemberNameMap([...boardMembers, ...members]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" className="task-board__card-modal">
      <ModalHeader toggle={onClose}>
        {card.title}
        {(card.isRecurrenceTemplate || card.isRecurrenceInstance) && (
          <i className="fas fa-redo ml-2 text-muted" style={{ fontSize: '0.8rem' }} title="Recurring card" />
        )}
      </ModalHeader>
      <ModalBody className="task-board__card-modal-body">
        <div className="task-board__card-modal-left">
          {(simpleLabels.length > 0 || labelsData.length > 0) && (
            <div className="task-board__modal-section" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
              <div className="task-board__simple-labels-row">
                {simpleLabels.map((label) => (
                  <span key={label} className="task-board__simple-label-badge task-board__simple-label-badge--readonly">
                    {label}
                  </span>
                ))}
                {labelsData.map((label, idx) => (
                  <span
                    key={`color-${idx}`}
                    className="task-board__chip task-board__chip--active"
                    style={{ backgroundColor: label.color || '#6b7280' }}
                  >
                    {label.text}
                  </span>
                ))}
              </div>
            </div>
          )}

          {dueBadge && (
            <div className="task-board__modal-section">
              <div className="small font-weight-bold text-muted mb-1 text-uppercase" style={{ fontSize: '0.7rem' }}>
                Due date
              </div>
              <span className={`task-board__card-pill task-board__card-pill--${dueBadge.variant}`}>
                <i className="far fa-calendar-alt mr-1" />
                {dueBadge.label}
              </span>
            </div>
          )}

          {card.description && (
            <div className="task-board__modal-section">
              <div className="small font-weight-bold text-muted mb-1 text-uppercase" style={{ fontSize: '0.7rem' }}>
                Description
              </div>
              <p className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: '#172b4d' }}>
                {card.description}
              </p>
            </div>
          )}

          {members.length > 0 && (
            <div className="task-board__modal-section">
              <div className="small font-weight-bold text-muted mb-1 text-uppercase" style={{ fontSize: '0.7rem' }}>
                Members
              </div>
              <div className="task-board__members-list" style={{ marginTop: 0 }}>
                {members.map((member) => {
                  const fullName = member.user
                    ? `${member.user.firstName} ${member.user.lastName}`.trim()
                    : member.userId;
                  const initials = member.user
                    ? `${member.user.firstName[0] ?? ''}${member.user.lastName[0] ?? ''}`.toUpperCase()
                    : '?';
                  return (
                    <div key={member.userId} className="task-board__member-card">
                      <span className="task-board__member-avatar">{initials}</span>
                      <span className="task-board__member-name">{fullName}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {checklists.length > 0 && (
            <div className="task-board__modal-section">
              <div className="small font-weight-bold text-muted mb-2 text-uppercase" style={{ fontSize: '0.7rem' }}>
                Checklists
              </div>
              {checklists.map((checklist, ci) => {
                const total = checklist.items.length;
                const done = checklist.items.filter((it) => it.isDone).length;
                return (
                  <div key={checklist.id ?? ci} className="task-board__checklist">
                    <div className="task-board__checklist-header">
                      <span className="task-board__checklist-title">{checklist.title}</span>
                      {total > 0 && (
                        <span className="badge badge-pill task-board__checklist-progress">
                          {done}/{total}
                        </span>
                      )}
                    </div>
                    {checklist.items
                      .slice()
                      .sort((a, b) => a.position - b.position)
                      .map((item, ii) => (
                        <div key={item.id ?? ii} className="task-board__checklist-item">
                          <input
                            type="checkbox"
                            className="task-board__checkbox"
                            checked={item.isDone}
                            readOnly
                            disabled
                          />
                          <span
                            style={{
                              fontSize: '0.875rem',
                              textDecoration: item.isDone ? 'line-through' : 'none',
                              color: item.isDone ? '#6b7280' : '#172b4d',
                              paddingLeft: '0.25rem',
                            }}
                          >
                            {item.text}
                          </span>
                        </div>
                      ))}
                  </div>
                );
              })}
            </div>
          )}

          {attachments.length > 0 && (
            <div className="task-board__modal-section">
              <div className="small font-weight-bold text-muted mb-2 text-uppercase" style={{ fontSize: '0.7rem' }}>
                Attachments
              </div>
              <ul className="list-unstyled mb-0">
                {attachments.map((att) => (
                  <li key={att.id} className="d-flex align-items-center mb-2">
                    <i className="fas fa-paperclip text-muted mr-2" style={{ fontSize: '0.8rem' }} />
                    <a href={att.signedUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem' }}>
                      {att.fileName}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="task-board__card-modal-right">
          <div className="task-board__comments">
            <div className="task-board__comments-title">
              <i className="far fa-comment-alt mr-1" />
              Comments
              {comments.length > 0 && (
                <span className="ml-1 text-muted" style={{ fontWeight: 400, fontSize: '0.78rem' }}>
                  ({comments.length})
                </span>
              )}
            </div>

            <div className="task-board__comments-list">
              {loadingComments ? (
                <div className="text-center text-muted py-3" style={{ fontSize: '0.84rem' }}>
                  Loading comments...
                </div>
              ) : comments.length === 0 ? (
                <div className="task-board__empty-state">No comments on this card.</div>
              ) : (
                comments.map((comment) => {
                  const firstName = comment.author?.firstName || '';
                  const lastName = comment.author?.lastName || '';
                  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
                  const authorName = `${firstName} ${lastName}`.trim() || 'Unknown';
                  return (
                    <div key={comment.id} className="task-board__comment">
                      <span className="task-board__comment-avatar">{initials}</span>
                      <div className="task-board__comment-body">
                        <div className="task-board__comment-meta">
                          <span className="task-board__comment-author">{authorName}</span>
                          <span className="task-board__comment-time">{formatCommentTime(comment.createdAt)}</span>
                        </div>
                        <div className="task-board__comment-bubble">
                          {renderCommentWithMentions(comment.text, mentionNameMap)}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose}>
          Close
        </Button>
        <Button color="primary" onClick={() => onRestore(card)}>
          <i className="fas fa-undo mr-1" />
          Restore to main board
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TaskBoardCardViewModal;
